"use client";

import {
  Dispatch,
  Ref,
  SetStateAction,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import { DateTime } from "luxon";

export default function CountdownTimer<T>({
  fields,
  setExpired,
  ref,
  targetDate = "1900",
}: {
  fields: (keyof T)[];
  ref: Ref<HTMLSpanElement>;
  setExpired: Dispatch<SetStateAction<T | null>>;
  targetDate?: string;
}) {
  const spanRef = useRef<HTMLSpanElement>(null);

  useImperativeHandle(ref, () => spanRef.current!);

  const calculateTimeLeft = () => {
    const targetTz = "America/Chicago";

    const utcDate = DateTime.fromISO(targetDate, { zone: targetTz }).toUTC();

    const difference = utcDate.toMillis() - Date.now();

    if (difference <= 0) return null;

    return {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / (1000 * 60)) % 60),
      seconds: Math.floor((difference / 1000) % 60),
    };
  };

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!timeLeft) {
      const undefinedFields = fields.reduce(
        (acc, field) => ({ ...acc, [field]: undefined }),
        {} as Partial<T>
      );

      setExpired((prev) => {
        return { ...(prev ?? {}), ...undefinedFields } as T;
      });
    }
  }, [timeLeft]);

  if (!timeLeft) return <span ref={spanRef}>0 minutes 0 seconds</span>;
  else
    return (
      <span ref={spanRef}>
        {timeLeft.minutes} minutes {timeLeft.seconds} seconds
      </span>
    );
}
